import { convertNumericColorToHex } from 'app/infrastructure/universal/color';
import { FavouriteRowValue } from '../common/favourite-interfaces';
import { FavouriteDataAdapter } from '../common/favourite-data-adapter';
import { ChartLayoutConfig } from '../common/layout-config';
import { DrillDownHandler } from '../common/drill-down-builder';
import { SeriesOptions } from 'app/components/favourite-layout/favourite/common/types';
import { DashStyleValue } from 'highcharts';

interface SynergiHighchartsPointObject extends Highcharts.PointOptionsObject {
  synDataRowIndex?: number;
}

export class ChartSeriesDataOptionsBuilder {
  private dataAdapter: FavouriteDataAdapter;
  private drillDownHandler: DrillDownHandler;

  constructor(dataAdapter: FavouriteDataAdapter, drillDownHandler: DrillDownHandler) {
    this.dataAdapter = dataAdapter;
    this.drillDownHandler = drillDownHandler;
  }

  public createSeriesOptions(
    seriesCol: number,
    seriesValues: FavouriteRowValue[],
    seriesNamePrefix: string = '',
    appendSeriesValues: boolean = true,
    typeStr: 'pie' | 'column' | 'line' | 'heatmap' = undefined,
    dashStyle: DashStyleValue = undefined
  ): SeriesOptions[] {
    const series: SeriesOptions[]  = [];

    for (let j = 0; j < seriesValues.length; j++) {
      const seriesObject: SeriesOptions = {
        name: this.makeSeriesName(seriesNamePrefix, this.dataAdapter.format(seriesCol, seriesValues[j].value), appendSeriesValues),
        data: [],
        type: typeStr,
        dashStyle: dashStyle
      };

      if (seriesValues[j].bgColour) {
        seriesObject.color = convertNumericColorToHex(seriesValues[j].bgColour);
      }

      this.addSeriesDrillDownHandler(this.drillDownHandler, seriesObject);
      this.addSpecialSeriesConfig(this.dataAdapter.layoutConfig as ChartLayoutConfig, seriesObject);
      series.push(seriesObject);
    }

    return series;
  }

  private addSeriesDrillDownHandler(drillDownHandler: DrillDownHandler, series: SeriesOptions) {
    if (drillDownHandler == null) {
      return;
    }

    series.cursor = 'pointer';
    series.events = {};
    series.events.click = (event) => {
      const rowIndex = (event.point as SynergiHighchartsPointObject).synDataRowIndex;
      drillDownHandler.toCaseListing(rowIndex);
    };
  }

  private makeSeriesName(prefix: string, value: string, append: boolean): string {
    if (prefix) {
      return prefix + (append ? ': ' + value : '');
    } else {
      return append ? value : '';
    }
  }

  private addSpecialSeriesConfig(config: ChartLayoutConfig, seriesObject: SeriesOptions, seriesType: string = 'mainSeries') {
    if (config.layoutAttributes.HighchartsSeries && config.layoutAttributes.HighchartsSeries[seriesType]) {
      Object.keys(config.layoutAttributes.HighchartsSeries[seriesType]).forEach((ind) => {
        (seriesObject as any)[ind] = config.layoutAttributes.HighchartsSeries[seriesType][ind];
      });
    }
  }

  public shouldAddColorToDataPoint(seriesIndex: number): boolean {
    return seriesIndex < 0; // Set colors directly on each data point if there are no series
  }

  // should be overridden with 'return true' if the dataLabels color should be adjusted to data point background color
  public shouldAdjustDataPointLabelColor(): boolean {
    return false;
  }
}
